import type { NotificationPreference } from "@lottolab/domain";

type OneSignalSdk = {
  init: (options: Record<string, unknown>) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  logout: () => Promise<void>;
  Notifications: {
    requestPermission: () => Promise<boolean>;
  };
  User: {
    addTags: (tags: Record<string, string>) => void;
  };
};

declare global {
  interface Window {
    OneSignalDeferred?: Array<(sdk: OneSignalSdk) => void | Promise<void>>;
  }
}

let scriptPromise: Promise<void> | null = null;
let initialized = false;

function loadSdk() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Không thể tải dịch vụ thông báo."));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function withSdk(callback: (sdk: OneSignalSdk) => void | Promise<void>) {
  window.OneSignalDeferred ??= [];
  window.OneSignalDeferred.push(callback);
}

export async function enableWebPush(
  userId: string,
  preferences: NotificationPreference[]
) {
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  if (!appId) throw new Error("OneSignal App ID chưa được cấu hình.");
  await loadSdk();
  return new Promise<void>((resolve, reject) => {
    withSdk(async (sdk) => {
      try {
        if (!initialized) {
          await sdk.init({
            appId,
            serviceWorkerPath: "OneSignalSDKWorker.js",
            serviceWorkerParam: { scope: "/push/" },
            allowLocalhostAsSecureOrigin: import.meta.env.DEV
          });
          initialized = true;
        }
        await sdk.login(userId);
        sdk.User.addTags(
          Object.fromEntries(
            preferences.flatMap((preference) => [
              [`prediction_${preference.gameCode}`, String(preference.predictionReady)],
              [`result_${preference.gameCode}`, String(preference.resultReady)]
            ])
          )
        );
        await sdk.Notifications.requestPermission();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  });
}

export function logoutWebPush() {
  if (!initialized) return;
  withSdk((sdk) => sdk.logout());
}
